import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import RightArrow from "../../../../Assets/Icons/RightArrow.png";
import LeftArrow from "../../../../Assets/Icons/LeftArrow.png";
import ExtraIcon from "../../../../Assets/Icons/ExtraIcon.png";
import Icons from "../../../../Assets/Icons";
import { fetchServiceItems, deleteServiceItem } from "../../../../modules/NursignCare/ManageServices/ManageServiceThunk";
import Widgets from "../../../../widgets";
import strings from "../../../../utils/strings";
import ToggleSwitch from "../../../../widgets/ToggleSwitch/ToggleSwitch";
import ToastPopup from "../../../../widgets/Toast/toastPopup";

const MRList = ({ searchValue, onEntrySummaryChange }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [clientPage, setClientPage] = useState(1);

  const { serviceItems, serviceItemsTotalDocuments } = useSelector((state) => state.manageService);

  const itemsPerPage = 10;

  // Fetch all records once; search/pagination handled client-side
  useEffect(() => {
    dispatch(fetchServiceItems({ page: 1, limit: 10000 }));
  }, []);

  // Reset to page 1 whenever search changes
  useEffect(() => {
    setClientPage(1);
  }, [searchValue]);

  const getLocaleString = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val;

    let lang = i18n.language?.toLowerCase();
    if (lang === "vietnam") lang = "vi";
    if (lang === "vn") lang = "vi";

    return val[lang] || val.en || val.vi || "";
  };

  const handleToggleItem = async (id, e) => {
    e.stopPropagation();
    const result = await dispatch(deleteServiceItem(id));
    if (result?.meta?.requestStatus === "fulfilled") {
      ToastPopup.success(t("serviceItemStatusUpdated") || "Status updated successfully");
      // Refresh service items to reflect the updated status
      dispatch(fetchServiceItems({ page: 1, limit: 10000 }));
    }
  };

  const filteredServiceItems = useMemo(() => {
    const q = (searchValue || "").trim().toLowerCase();
    return (serviceItems || [])
      .filter((item) => {
        if (!q) return true;
        const group = getLocaleString(item.service?.title).toLowerCase();
        return group.includes(q);
      })
      .sort((a, b) => {
        // Active items first, then inactive
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
        return getLocaleString(a.title).localeCompare(getLocaleString(b.title), undefined, { sensitivity: "base" });
      });
  }, [serviceItems, searchValue, i18n.language]);

  const totalClientPages = Math.max(1, Math.ceil(filteredServiceItems.length / itemsPerPage));
  const pagedItems = filteredServiceItems.slice((clientPage - 1) * itemsPerPage, clientPage * itemsPerPage);

  useEffect(() => {
    onEntrySummaryChange?.({
      displayed: filteredServiceItems.length,
      total: serviceItemsTotalDocuments || (serviceItems || []).length,
    });
  }, [filteredServiceItems.length, serviceItemsTotalDocuments, onEntrySummaryChange]);

  const renderPagination = () => {
    const pages = [];
    if (totalClientPages <= 4) {
      for (let i = 1; i <= totalClientPages; i++) pages.push(i);
    } else if (clientPage <= 2) {
      pages.push(1, 2, { type: "ellipsis" }, totalClientPages);
    } else if (clientPage >= totalClientPages - 1) {
      pages.push(1, { type: "ellipsis" }, totalClientPages - 1, totalClientPages);
    } else {
      pages.push(1, { type: "ellipsis" }, clientPage, { type: "ellipsis" }, totalClientPages);
    }
    return pages;
  };

  return (
    <div className="nc-mr-secondary-box">
      <div className="nc-mr-price-box">
        <table className="nc-nr-price-layout">
          <thead className="nc-mr-thead">
            <tr>
              <th style={{ width: "35%" }}>{t("serviceItem")}</th>
              <th style={{ width: "15%" }}>{t("serviceCode")}</th>
              <th className="nc-ms-status-col" style={{ width: "15%" }}>{t("status")}</th>
              <th style={{ width: "20%" }}>{t("id")}</th>
              <th className="nc-ms-action-col" style={{ width: "15%" }}>{t("action") || "Action"}</th>
            </tr>
          </thead>
          <tbody className="nc-mr-tbody">
            {pagedItems.map((item) => {
              const code = item.code || "-";
              const serviceItem = getLocaleString(item.title) || "-";

              return (
                <tr
                  key={item._id}
                  className="nc-mr-table-row"
                  onClick={() => {
                    navigate(strings.navigation.serviceDetail, {
                      state: item,
                    });
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <td title={serviceItem}>{serviceItem}</td>
                  <td title={code}>{code}</td>
                  <td className="nc-ms-status-col">
                    <span className={`status-pill ${item.isActive ? "status-paid" : "status-absconded"}`}>
                      {item.isActive ? t("active") : t("inactive")}
                    </span>
                  </td>
                  <td>
                    <span
                      className="nc-copy-action"
                      title={t("copyId")}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(item._id);
                        Widgets.ToastPopup.success(t("idCopied"));
                      }}
                    >
                      {t("copy")}
                    </span>
                  </td>
                  <td className="nc-ms-action-col">
                    <ToggleSwitch
                      isOn={item.isActive}
                      handleToggle={(e) => handleToggleItem(item._id, e)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {pagedItems.length === 0 && (
          <div className="no-record-container">
            <div className="no-record-icon">
              <img src={Icons.NoData} alt="No Data Found" />
            </div>
            <div className="no-record-text">{t("noRecordFound")}</div>
          </div>
        )}
        <div className="nc-mr-pagination-list">
          {totalClientPages > 1 && (
            <>
              <button
                onClick={() => setClientPage((p) => Math.max(1, p - 1))}
                disabled={clientPage === 1}
                className="nc-mr-pagination-prev-list"
              >
                <img src={LeftArrow} alt="<" />
              </button>
              {renderPagination().map((page, index) => (
                <button
                  key={index}
                  onClick={() => typeof page === "number" && setClientPage(page)}
                  disabled={page === clientPage || page?.type === "ellipsis"}
                  className={`nc-mr-pagination-btn-list ${page === clientPage ? "active-list" : ""} ${page?.type === "ellipsis" ? "ellipse" : ""}`}
                >
                  {typeof page === "number" ? page : <img src={ExtraIcon} alt="..." className="department-extra-icon" />}
                </button>
              ))}
              <button
                onClick={() => setClientPage((p) => Math.min(totalClientPages, p + 1))}
                disabled={clientPage === totalClientPages}
                className="nc-mr-pagination-next-list"
              >
                <img src={RightArrow} alt=">" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MRList;
